import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Chip,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  DialogContentText,
  Menu, // Add Menu import
  ListItemIcon, // Add ListItemIcon import
  ListItemText, // Add ListItemText import
  Radio, // Add Radio import
  RadioGroup, // Add RadioGroup import
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import TypeWriter from "../../components/common/TypeWriter";
import LinkedInPreview from "../../components/preview/LinkedInPreview";
import { useAuth } from "../../context/AuthContext";
import { Save as SaveIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import ImageIcon from "@mui/icons-material/Image";
import aiService from "../../services/aiService";
import { useCredits } from "../../hooks/useCredits";
import UpgradeOverlay from "../../components/common/UpgradeOverlay";
import { eventService } from "../../services/eventService";
import WelcomePopup from "../../components/common/WelcomePopup";
import LinkedInShareButton from "../../components/social/LinkedInShareButton";
import { useSnackbar } from "notistack";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LinkedInConnectDialog from "../../components/dialogs/LinkedInConnectDialog";
import { Edit as EditIcon } from "@mui/icons-material";
import PostEditor from "../../components/editor/PostEditor";
import { convertFromRaw } from "draft-js";
import AnimatedLogo from "../../components/common/AnimatedLogo";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import {
  // ... existing imports
  AutoAwesome as MagicIcon,
  YouTube as YouTubeIcon,
  AutoAwesome as AutoAwesomeIcon,
  Upload as UploadIcon,
  InfoOutlined as InfoOutlinedIcon,
  AttachFile as AttachFileIcon, // Added for carousel
  AddPhotoAlternate as AddPhotoAlternateIcon, // Added for image
  Tune as TuneIcon, // Add this import for modification options
} from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import templates from "../../data/templates.js";
import LoadingScreen from "../../components/common/LoadingScreen";
import { Helmet } from "react-helmet-async";
import ContentScorer from "../../components/content-scoring/ContentScorer";

// Import the modal components
import GenerateImage from "../GenerateImage";
import GenerateCarousel from "../GenerateCarousel";
import { handleImageUpload } from "./imageAnalysisService";
import {
  handleTemplateClick,
  handleUseTemplate,
  handleGenerateFromTemplate,
  handleViewFullTemplate,
} from "./templateService";
import {
  handleArticleSubmit,
  handleGenerateFromArticle,
} from "./articleService";
import {
  extractUsername,
  fetchUserLinkedInPosts,
  analyzeToneWithGemini,
  formatResponse,
  handleGenerate,
  handleChange,
} from "./customGenerationService";
import { cleanHtmlEntities } from "./utils/textUtils";
import toast from "react-hot-toast"; // Added import

const GeneratePost = () => {
  const { user, updateUser } = useAuth();
  const { credits, refetchCredits } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const [userProfile, setUserProfile] = useState(null);
  const [showUpgradeOverlay, setShowUpgradeOverlay] = useState(false);
  const [postType, setPostType] = useState("educational");
  const [tone, setTone] = useState("professional");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedContent, setGeneratedContent] = useState("");
  const [generatedHashtags, setGeneratedHashtags] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [postImage, setPostImage] = useState(""); // This will store the URL of the attached image
  const [carouselFile, setCarouselFile] = useState(null); // This will store the File/Blob of the attached carousel
  const [posts, setPosts] = useState([]);
  const [postsDialogOpen, setPostsDialogOpen] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isSharing, setIsSharing] = useState(false);
  const [showLinkedInConnect, setShowLinkedInConnect] = useState(false);
  const [articleUrl, setArticleUrl] = useState("");
  const [analyzingArticle, setAnalyzingArticle] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const previewRef = useRef(null);
  const [profDetailsLoading, setProfDetailsLoading] = useState(false);
  const [profDetailsSuccess, setProfDetailsSuccess] = useState("");
  const [advancedSettingsEnabled, setAdvancedSettingsEnabled] = useState(false);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateTopic, setTemplateTopic] = useState("");
  const [templateCategory, setTemplateCategory] = useState("viral");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("custom");
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [fullTemplateText, setFullTemplateText] = useState("");
  const [fullTemplateTitle, setFullTemplateTitle] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeError, setYoutubeError] = useState("");
  const [youtubeData, setYoutubeData] = useState(null);
  // Add state for YouTube subtitles
  const [youtubeSubtitles, setYoutubeSubtitles] = useState(null);
  const [subtitlesLoading, setSubtitlesLoading] = useState(false);
  const [subtitlesError, setSubtitlesError] = useState("");
  // Add a new state for topic loading
  const [topicLoading, setTopicLoading] = useState(false);
  // Add these new state variables
  const [articleLoading, setArticleLoading] = useState(false);
  const [articleError, setArticleError] = useState("");
  const [articleContent, setArticleContent] = useState(null);
  // First, add a new state for subtitle error
  const [subtitlesNotFound, setSubtitlesNotFound] = useState(false);
  const [showArticlePreview, setShowArticlePreview] = useState(false);
  const [useAdvancedSettings, setUseAdvancedSettings] = useState(false);
  const [editedTopic, setEditedTopic] = useState("");
  const [imageGeneratedTopic, setImageGeneratedTopic] = useState(""); // New state for image-generated topic

  // State for modals
  const [showImageModal, setShowImageModal] = useState(false);
  const [showCarouselModal, setShowCarouselModal] = useState(false);
  const [showAttachmentConfirmDialog, setShowAttachmentConfirmDialog] =
    useState(false);
  const [pendingAttachmentType, setPendingAttachmentType] = useState(null);

  // Near the top with other refs
  const templateTopicRef = useRef(null);

  // Add new state for post modification
  const [showModificationOptions, setShowModificationOptions] = useState(false);
  const [modificationLoading, setModificationLoading] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  const [showCustomInstructionsInput, setShowCustomInstructionsInput] =
    useState(false);
  const [modificationAnchorEl, setModificationAnchorEl] = useState(null); // Add anchor element for menu

  // Tone analysis states
  const [toneAnalysisEnabled, setToneAnalysisEnabled] = useState(true);
  const [userLinkedInPosts, setUserLinkedInPosts] = useState([]);
  const [toneAnalysisLoading, setToneAnalysisLoading] = useState(false);
  const [analyzedTone, setAnalyzedTone] = useState("");
  const [manualPostsText, setManualPostsText] = useState("");

  // Content preferences states
  const [contentPreferencesEnabled, setContentPreferencesEnabled] =
    useState(false);
  const [includeEmojis, setIncludeEmojis] = useState(false);
  const [includeHashtags, setIncludeHashtags] = useState(false);
  const [contentLength, setContentLength] = useState("default"); // default, short, medium, long

  // Model selection state
  const [selectedModel, setSelectedModel] = useState("in-model-a"); // in-model-a or in-model-b

  console.log("GeneratePost user data:", {
    fullUser: user,
    nestedUser: user?.user,
    image: user?.user?.image || user?.image,
    name: user?.user?.name || user?.name,
    headline: user?.user?.headline || user?.headline,
  });

  const [formData, setFormData] = useState({
    topic: location.state?.autoFill ? location.state.topic : "",
    writingStyle: null,
    goals: [],
    industry: "",
    professionalRole: "",
    targetAudience: "",
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const userData = response.data.user;
        setUserProfile(userData);

        // Update formData with professional details from user profile
        setFormData((prev) => ({
          ...prev,
          industry: userData.industry || "",
          professionalRole: userData.professionalRole || "",
          targetAudience: userData.targetAudience || "",
          timezone:
            userData.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            "America/New_York",
        }));
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (location.state?.autoFill && location.state.topic) {
      setFormData((prev) => ({
        ...prev,
        topic: location.state.topic,
      }));
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const savedImage = localStorage.getItem("generatedImage");
    if (savedImage) {
      setPostImage(savedImage);
      localStorage.removeItem("generatedImage");
    }
  }, []);

  useEffect(() => {
    if (user && user.is_new && !localStorage.getItem("welcomeShown")) {
      // console.log('GeneratePost - Full user object:', user);
      // console.log('GeneratePost - is_new value:', user?.is_new);
      // console.log('GeneratePost - localStorage welcomeShown:', localStorage.getItem('welcomeShown'));

      // console.log('GeneratePost - Should show welcome popup');
      setShowWelcome(true);
      localStorage.setItem("welcomeShown", "true");
    }

    const checkLinkedInStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/linkedin-status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        // console.log('LinkedIn status in GeneratePost:', response.data);
        setShowLinkedInConnect(!response.data.isConnected);
      } catch (error) {
        console.error("Error checking LinkedIn status:", error);
      }
    };

    checkLinkedInStatus();
  }, [user]);

  // Function to extract username from LinkedIn profile URL
  const extractUsername = (url) => {
    if (!url) return null;

    try {
      // Remove protocol and www
      let cleanUrl = url.replace(/^https?:\/\/(www\.)?/, "");

      // Remove trailing slash
      cleanUrl = cleanUrl.replace(/\/$/, "");

      // Extract username from LinkedIn URL patterns
      if (cleanUrl.includes("linkedin.com/in/")) {
        const match = cleanUrl.match(/linkedin\.com\/in\/([^\/\?]+)/);
        return match ? match[1] : null;
      }

      // Handle other LinkedIn URL patterns
      if (cleanUrl.includes("linkedin.com/pub/")) {
        const match = cleanUrl.match(/linkedin\.com\/pub\/([^\/\?]+)/);
        return match ? match[1] : null;
      }

      return null;
    } catch (error) {
      console.error("Error extracting username:", error);
      return null;
    }
  };

  // Function to fetch user's LinkedIn posts
  const fetchUserLinkedInPosts = async () => {
    if (!userProfile?.linkedinProfile) {
      console.error("No LinkedIn profile found for user");
      return [];
    }

    try {
      setToneAnalysisLoading(true);
      const username = extractUsername(userProfile.linkedinProfile);

      if (!username) {
        console.error("Could not extract username from LinkedIn profile");
        return [];
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/linkedin/profile/posts`,
        {
          params: { username },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      let postsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          postsData = response.data;
        } else if (response.data.posts && Array.isArray(response.data.posts)) {
          postsData = response.data.posts;
        }
      }
      setUserLinkedInPosts(postsData);
      return postsData;
    } catch (error) {
      console.error("Error fetching user LinkedIn posts:", error);
      setError("Failed to fetch your LinkedIn posts for tone analysis");
      return [];
    } finally {
      setToneAnalysisLoading(false);
    }
  };

  // Function to analyze tone using Gemini
  const analyzeToneWithGemini = async (posts) => {
    if (!posts || posts.length === 0) {
      console.error("No posts provided for tone analysis");
      return "";
    }

    try {
      setToneAnalysisLoading(true);

      // Take the most recent 10 posts for analysis (to avoid token limits)
      const postsToAnalyze = posts.slice(0, 10);
      const postContents = postsToAnalyze
        .map((post) => post.content || "")
        .filter((content) => content.trim().length > 0)
        .join("\n\n---\n\n");

      if (!postContents) {
        console.error("No post content found for tone analysis");
        return "";
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/analyze-tone`,
        {
          content: postContents,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const analyzedToneResult = response.data.analysis || "";
      setAnalyzedTone(analyzedToneResult);
      return analyzedToneResult;
    } catch (error) {
      console.error("Error analyzing tone:", error);
      setError("Failed to analyze your writing tone");
      return "";
    } finally {
      setToneAnalysisLoading(false);
    }
  };

  const analyzeManualTone = async () => {
    if (!manualPostsText.trim()) return;
    await analyzeToneWithGemini([{ content: manualPostsText.trim() }]);
  };

  // Clear tone when disabled
  useEffect(() => {
    if (!toneAnalysisEnabled) setAnalyzedTone("");
  }, [toneAnalysisEnabled]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // console.log(`Form field changed: ${name} = `, value);

    if (name === "writingStyle") {
      // console.log("Writing style change event:", e);
      setFormData({ ...formData, writingStyle: value });
    } else {
      const newData = { ...formData, [name]: value };
      // console.log("Updated formData:", newData);
      setFormData(newData);
    }
  };

  const formatResponse = (data) => {
    let content = "";

    // Try to extract content from various response formats
    if (data.content) {
      // console.log('Found content in response.data.content');
      content = data.content;
    } else if (typeof data === "string") {
      // console.log('Response was a direct string');
      content = data;
    } else if (data.post) {
      content = data.post;
    } else if (data.text) {
      // console.log('Found content as a string property in response');
      content = data.text;
    } else {
      // As a last resort, try to convert the entire response to a string
      // console.log('Converted entire response to string');
      content = JSON.stringify(data);
    }

    return content;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    handleGenerateWrapper();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([generatedContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `linkedin-post-${
      new Date().toISOString().split("T")[0]
    }.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleRegenerate = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await aiService.generateContent(formData);
      if (!response.data) {
        throw new Error("No data received from AI service");
      }

      const formattedContent = formatResponse(response.data);
      setGeneratedContent(formattedContent);
      eventService.emit("CREDITS_UPDATED");
    } catch (err) {
      console.error("Regeneration error:", err);
      setError(
        err.message || "Failed to regenerate content. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSavePost = async () => {
    if (!generatedContent) return;

    setSaveLoading(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      let postId = null; // To store post ID if we need to attach carousel later

      // Step 1: Save the main post content (and image if any)
      const postData = {
        content: generatedContent,
        topic: formData.topic || "Custom Post",
        type: "standard", // Or determine type based on attachments
        image: postImage || null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/posts`,
        postData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      postId = response.data?.id || response.data?.post?.id; // Adjust based on your API response structure

      enqueueSnackbar("Draft saved successfully!", { variant: "success" });
      setSaveSuccess("Draft saved successfully!");

      // Step 2: If carousel file exists and postId is available, attach it
      if (carouselFile && postId) {
        const carouselFormData = new FormData();
        carouselFormData.append(
          "file",
          carouselFile,
          carouselFile.name || "carousel.pdf"
        );

        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/posts/${postId}/attachment`,
          carouselFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        enqueueSnackbar("Carousel attached successfully!", {
          variant: "success",
        });
        setCarouselFile(null); // Clear after successful attachment
      }

      // Clear postImage if it was part of this save, or manage as needed
      // setPostImage(''); // Optional: Clear image after save
    } catch (err) {
      console.error("Error saving draft:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to save draft";
      setSaveError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaveLoading(false);
      setTimeout(() => {
        setSaveSuccess("");
        setSaveError("");
      }, 4000);
    }
  };

  const handleGenerateImage = () => {
    const imagePrompt =
      formData.prompt || `Create an image about ${formData.topic}`;
    if (imagePrompt) {
      localStorage.setItem("postPrompt", imagePrompt);
      navigate("/dashboard/generate-image");
    }
  };

  const handleGenerateWrapper = async () => {
    // Create the parameters object for the separated service
    const params = {
      formData,
      activeTab,
      imageGeneratedTopic,
      youtubeSubtitles,
      articleContent,
      templateTopic,
      selectedTemplate,
      toneAnalysisEnabled,
      userLinkedInPosts,
      analyzedTone,
      useAdvancedSettings,
      selectedModel,
      includeEmojis,
      includeHashtags,
      contentLength,
      contentPreferencesEnabled,
      setError,
      setLoading,
      setGeneratedContent,
      setAnalyzedTone,
    };

    // Call the separated service function
    await handleGenerate(params);
  };

  useEffect(() => {
    if (credits === 0) {
      setShowUpgradeOverlay(true);
    }
  }, [credits]);

  const handleLinkedInSuccess = (message) => {
    toast.success(message); // Use the same success handler as saving posts
  };

  const handleLinkedInError = (error) => {
    toast.error(error); // Use the same error handler as saving posts
  };

  const handleSuccess = (message) => {
    toast.success(message); // Updated to use toast
  };

  const handleError = (message) => {
    toast.error(message); // Updated to use toast
  };

  const handleLinkedInShare = async () => {
    if (!generatedContent) return;

    try {
      if (showLinkedInConnect) {
        // console.log('LinkedIn not connected, showing connect dialog');
        setShowLinkedInConnect(true);
      } else {
        // console.log('LinkedIn connected, sharing post');
        await handleShareToLinkedIn();
      }
    } catch (error) {
      console.error("Error with LinkedIn share:", error);
    }
  };

  const handleShareToLinkedIn = async () => {
    if (!generatedContent) return;

    try {
      setIsSharing(true);

      // Prepare the payload based on post type
      const payload = {
        text: generatedContent,
        image: null,
        attachment: null,
      };

      // If there's a carousel file, convert it to base64 and include it
      if (carouselFile) {
        // Convert the PDF to base64
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => {
            // Get base64 string without the data URL prefix
            const base64 = reader.result.split(",")[1];
            resolve(base64);
          };
          reader.onerror = reject;
        });

        reader.readAsDataURL(carouselFile);
        payload.attachment = await base64Promise;
        payload.title = "Like, Share & Repost"; // Add title for carousel posts
      } else if (postImage) {
        // If there's an image, include it directly - no need for base64 conversion
        // since postImage is already a URL or base64 string
        payload.image = postImage;
      }

      console.log("Sharing post to LinkedIn:", {
        hasCarousel: !!carouselFile,
        hasImage: !!postImage,
        imageType: typeof postImage,
      });

      // Call LinkedIn share service
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/linkedin/share`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("LinkedIn share response:", response.data);

      // Show success notification
      enqueueSnackbar("Successfully shared to LinkedIn!", {
        variant: "success",
        autoHideDuration: 4000,
      });
    } catch (error) {
      console.error("LinkedIn share error:", error);

      // Show error notification with specific message if available
      enqueueSnackbar(
        error.response?.data?.message || "Failed to share to LinkedIn",
        {
          variant: "error",
          autoHideDuration: 4000,
        }
      );
    } finally {
      setIsSharing(false);
    }
  };

  // Handle generating carousel from post content
  const handleGenerateCarousel = async () => {
    if (!generatedContent) return;

    try {
      setCarouselLoading(true);

      // Clear any previous carousel slides from localStorage
      localStorage.removeItem("carouselSlides");

      // Call our backend API instead of directly calling the third-party service
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/generate-carousel`,
        { content: generatedContent },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const { success, slides } = response.data;

      if (success && slides && slides.length > 0) {
        // Store the slides in localStorage to pass to carousel generator
        localStorage.setItem("carouselSlides", JSON.stringify(slides));

        // Navigate to carousel generator with timestamp to force reload
        navigate("/dashboard/generate-carousel?t=" + Date.now());

        enqueueSnackbar(
          "Carousel content generated! Redirecting to carousel editor...",
          {
            variant: "success",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          }
        );
      } else {
        throw new Error("No slide content received from API");
      }
    } catch (error) {
      console.error("Carousel generation error:", error);
      enqueueSnackbar(
        "Failed to generate carousel content: " +
          (error.response?.data?.message || error.message || "Unknown error"),
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        }
      );
    } finally {
      setCarouselLoading(false);
    }
  };

  const handleSaveProfessionalDetails = async () => {
    setProfDetailsLoading(true);
    setProfDetailsSuccess("");

    try {
      // Ensure all values are either strings or null, not undefined
      const professionalData = {
        industry: formData.industry || null,
        professionalRole: formData.professionalRole || null,
        targetAudience: formData.targetAudience || null,
        timezone: formData.timezone || null,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
        professionalData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Update the user data in context if needed
      if (response.data.success) {
        setProfDetailsSuccess("Professional details updated successfully");
      }
    } catch (err) {
      console.error("Error updating professional details:", err);
      setError("Failed to update professional details");
    } finally {
      setProfDetailsLoading(false);
    }
  };

  // Function to handle template selection
  const handleTemplateClickLocal = (template) => {
    handleTemplateClick(
      template,
      setSelectedTemplate,
      setTemplateTopic,
      setGeneratedContent,
      setFullTemplateTitle,
      setFullTemplateText,
      setOpenTemplateDialog
    );
  };

  // Function to handle "Use This Template" button click
  const handleUseTemplateLocal = (template) => {
    handleUseTemplate(
      template,
      setSelectedTemplate,
      setTemplateTopic,
      setGeneratedContent,
      setOpenTemplateDialog,
      templateTopicRef
    );
  };

  // Function to generate content from template
  const handleGenerateFromTemplateLocal = async () => {
    await handleGenerateFromTemplate(
      selectedTemplate,
      templateTopic,
      setTemplateLoading,
      setGeneratedContent,
      setSelectedTemplate,
      setTemplateTopic
    );
  };

  const handleViewFullTemplateLocal = (e, template) => {
    handleViewFullTemplate(
      e,
      template,
      setFullTemplateTitle,
      setFullTemplateText,
      setOpenTemplateDialog
    );
  };

  // Replace the handleYoutubeSubmit function with this combined function
  const handleYoutubeSubmit = async (e) => {
    e.preventDefault();
    if (!youtubeUrl) {
      setYoutubeError("Please enter a YouTube URL");
      toast.error("Please enter a YouTube URL"); // Added toast
      return;
    }

    setYoutubeLoading(true);
    setYoutubeError("");
    setYoutubeData(null);
    setSubtitlesNotFound(false); // Reset the error state
    setLoading(true);

    try {
      console.log(
        "Processing YouTube URL and generating content directly:",
        youtubeUrl
      );

      // Show more user feedback
      toast.loading("Processing YouTube URL, this may take a moment..."); // Replaced enqueueSnackbar with toast.loading

      // Step 1: Call our backend API to get YouTube info
      const infoResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/youtube/info`,
        {
          params: { url: youtubeUrl },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Response from backend YouTube proxy:", infoResponse.data);

      // Set the extracted data from our backend
      if (!infoResponse.data) {
        throw new Error("No data received from backend");
      }

      const youtubeData = infoResponse.data;
      setYoutubeData(youtubeData);

      // Validate parameter lengths
      if (
        !youtubeData.signature ||
        !youtubeData.expireTime ||
        !youtubeData.ei ||
        !youtubeData.opi
      ) {
        throw new Error(
          "Missing one or more required YouTube parameters. Please try again."
        );
      }

      // Validate and clean the ei parameter if needed
      if (youtubeData.ei && youtubeData.ei.length > 35) {
        console.warn("ei parameter seems unusually long:", youtubeData.ei);
        if (youtubeData.ei.includes("\\u0026")) {
          const cleanEi = youtubeData.ei.split("\\u0026")[0];
          console.log("Extracted clean ei from longer value:", cleanEi);
          youtubeData.ei = cleanEi;
        }
      }

      const videoId = extractVideoId(youtubeUrl);
      if (!videoId) {
        throw new Error("Could not extract video ID from URL");
      }

      // Step 2: Fetch the subtitles
      toast.loading("Fetching YouTube subtitles..."); // Replaced enqueueSnackbar with toast.loading

      const subtitlesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/youtube/subtitles`,
        {
          params: {
            videoId,
            signature: youtubeData.signature,
            expire: youtubeData.expireTime,
            ei: youtubeData.ei,
            opi: youtubeData.opi,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          timeout: 15000,
        }
      );

      // Check if subtitles are empty or not found
      if (
        !subtitlesResponse.data ||
        (typeof subtitlesResponse.data === "string" &&
          subtitlesResponse.data.trim() === "") ||
        (Array.isArray(subtitlesResponse.data) &&
          subtitlesResponse.data.length === 0)
      ) {
        setSubtitlesNotFound(true);
        throw new Error("No subtitles found for this video");
      }

      console.log("Subtitles response:", {
        status: subtitlesResponse.status,
        contentType: subtitlesResponse.headers["content-type"],
        dataLength: subtitlesResponse.data
          ? typeof subtitlesResponse.data === "string"
            ? subtitlesResponse.data.length
            : JSON.stringify(subtitlesResponse.data).length
          : 0,
        data: subtitlesResponse.data
          ? typeof subtitlesResponse.data === "string"
            ? subtitlesResponse.data.substring(0, 100) + "..."
            : subtitlesResponse.data
          : null,
      });

      // Set the subtitles data
      const youtubeSubtitles = subtitlesResponse.data;
      setYoutubeSubtitles(youtubeSubtitles);

      // Step 3: Generate the post from subtitles
      toast.loading("Generating post from subtitles..."); // Replaced enqueueSnackbar with toast.loading

      // Extract text from XML
      let subtitlesText = "";

      if (youtubeSubtitles.includes("<text ")) {
        // This is a simple XML parser - might need improvement for robustness
        const regex = /<text [^>]*>(.*?)<\/text>/g;
        let match;
        while ((match = regex.exec(youtubeSubtitles)) !== null) {
          subtitlesText += match[1] + " ";
        }

        // Clean HTML entities using the utility function
        subtitlesText = cleanHtmlEntities(subtitlesText);
      } else {
        subtitlesText =
          typeof youtubeSubtitles === "string"
            ? youtubeSubtitles
            : JSON.stringify(youtubeSubtitles, null, 2);
      }

      // Prepare data to send to InstaImage API
      const dataToSend = {
        bio: subtitlesText || "",
        vibe: formData.writingStyle || null,
        goals: formData.goals || [],
        industry: formData.industry || null,
        role: formData.professionalRole || null,
        targetAudience: formData.targetAudience || null,
        isFromYouTube: true, // Additional flag to indicate source is YouTube subtitles
      };

      console.log("Sending YouTube subtitles to API:", dataToSend);

      // Call InstaImage API
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/generate`,
        dataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "Response from InstaImage (YouTube subtitles):",
        response.data
      );

      // Extract content correctly - similar to handleGenerate
      let content = "";

      if (response.data && response.data.content) {
        // Most likely format: {"content": "the generated text here"}
        content = response.data.content;
        console.log("Found content in response.data.content");
      } else if (typeof response.data === "string") {
        // If the response is directly a string
        content = response.data;
        console.log("Response was a direct string");
      } else if (response.data && typeof response.data === "object") {
        // Try to extract content from other possible response formats
        const possibleContent = Object.values(response.data).find(
          (val) => typeof val === "string" && val.length > 100
        );

        if (possibleContent) {
          content = possibleContent;
          console.log("Found content as a string property in response");
        } else {
          // Last resort: stringify the whole response
          content = JSON.stringify(response.data);
          console.log("Converted entire response to string");
        }
      }

      // Format the content to ensure proper display
      content = formatResponse({ content });

      // Set the generated content in state
      setGeneratedContent(content);

      // Handle hashtags if they exist
      if (response.data.hashtags) {
        setGeneratedHashtags(response.data.hashtags);
      }

      // Emit event for credit tracking
      eventService.emit("CREDITS_UPDATED");

      toast.dismiss(); // Dismiss loading toasts
      toast.success("Post generated from YouTube subtitles!"); // Replaced enqueueSnackbar

      // Scroll to preview
      setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    } catch (err) {
      console.error("YouTube processing error:", err);
      setSubtitlesNotFound(true);
      let displayErrorMessage =
        "Failed to process YouTube URL. Please try again.";

      if (err.response && err.response.status === 404) {
        displayErrorMessage =
          "Video not found or subtitles are unavailable. Please check the URL or try a different video.";
      } else if (err.message) {
        displayErrorMessage = err.message;
      }

      setYoutubeError(displayErrorMessage);
      toast.dismiss(); // Dismiss any active loading toasts
      toast.error(displayErrorMessage);
    } finally {
      setYoutubeLoading(false);
      setLoading(false);
    }
  };

  // Add function to extract video ID from YouTube URL
  const extractVideoId = (url) => {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  // Add this function after handleFetchSubtitles
  const handleGenerateFromSubtitles = async () => {
    if (!youtubeSubtitles) {
      toast.error("Please fetch subtitles first"); // Replaced enqueueSnackbar
      return;
    }

    setLoading(true);
    setError("");
    let loadingToastId = null; // For managing loading toast

    try {
      loadingToastId = toast.loading("Generating post from subtitles..."); // Added loading toast
      // Extract text from XML
      let subtitlesText = "";

      if (youtubeSubtitles.includes("<text ")) {
        // This is a simple XML parser - might need improvement for robustness
        const regex = /<text [^>]*>(.*?)<\/text>/g;
        let match;
        while ((match = regex.exec(youtubeSubtitles)) !== null) {
          subtitlesText += match[1] + " ";
        }

        // Clean HTML entities using the utility function
        subtitlesText = cleanHtmlEntities(subtitlesText);
      } else {
        subtitlesText =
          typeof youtubeSubtitles === "string"
            ? youtubeSubtitles
            : JSON.stringify(youtubeSubtitles, null, 2);
      }

      // Prepare data to send to InstaImage API
      const dataToSend = {
        bio: subtitlesText || "",
        vibe: formData.writingStyle || null,
        goals: formData.goals || [],
        industry: formData.industry || null,
        role: formData.professionalRole || null,
        targetAudience: formData.targetAudience || null,
        isFromYouTube: true, // Additional flag to indicate source is YouTube subtitles
      };

      console.log("Sending YouTube subtitles to API:", dataToSend);

      // Call InstaImage API
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/generate`,
        dataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "Response from InstaImage (YouTube subtitles):",
        response.data
      );

      // Extract content correctly - similar to handleGenerate
      let content = "";

      if (response.data && response.data.content) {
        // Most likely format: {"content": "the generated text here"}
        content = response.data.content;
        console.log("Found content in response.data.content");
      } else if (typeof response.data === "string") {
        // If the response is directly a string
        content = response.data;
        console.log("Response was a direct string");
      } else if (response.data && typeof response.data === "object") {
        // Try to extract content from other possible response formats
        const possibleContent = Object.values(response.data).find(
          (val) => typeof val === "string" && val.length > 100
        );

        if (possibleContent) {
          content = possibleContent;
          console.log("Found content as a string property in response");
        } else {
          // Last resort: stringify the whole response
          content = JSON.stringify(response.data);
          console.log("Converted entire response to string");
        }
      }

      // Format the content to ensure proper display
      content = formatResponse({ content });

      // Set the generated content in state
      setGeneratedContent(content);

      // Handle hashtags if they exist
      if (response.data.hashtags) {
        setGeneratedHashtags(response.data.hashtags);
      }

      // Emit event for credit tracking
      eventService.emit("CREDITS_UPDATED");

      toast.dismiss(loadingToastId); // Dismiss specific loading toast
      toast.success("Post generated from YouTube subtitles!"); // Replaced enqueueSnackbar

      // Scroll to preview
      setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    } catch (err) {
      console.error("Error generating post from subtitles:", err);

      // Log detailed error information
      if (err.response) {
        console.error("Error response data:", err.response.data);
        console.error("Error response status:", err.response.status);
      }

      if (err.response?.status === 402) {
        setError("You have insufficient credits. Please upgrade your plan.");
      } else {
        setError("Failed to generate post from subtitles. Please try again.");
      }

      toast.dismiss(loadingToastId); // Dismiss specific loading toast
      toast.error("Error generating post from subtitles"); // Replaced enqueueSnackbar
    } finally {
      setLoading(false);
    }
  };

  // Add this function to handle image upload and use backend API
  const handleImageUploadEvent = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      await handleImageUpload(
        file,
        setTopicLoading,
        setImageGeneratedTopic,
        setEditedTopic,
        enqueueSnackbar
      );
    } catch (error) {
      // Error handling is already done in the imported function
      console.error("Image upload failed:", error);
    }
  };

  const handleArticleSubmitLocal = async (e) => {
    await handleArticleSubmit(
      e,
      articleUrl,
      setArticleError,
      setArticleLoading,
      setLoading,
      setArticleContent,
      setShowArticlePreview,
      enqueueSnackbar
    );
  };

  const handleGenerateFromArticleLocal = async () => {
    await handleGenerateFromArticle(
      articleContent,
      formData,
      useAdvancedSettings,
      includeEmojis,
      includeHashtags,
      contentLength,
      setLoading,
      setError,
      setGeneratedContent,
      setGeneratedHashtags,
      formatResponse,
      previewRef
    );
  };

  const handleAttachImageModal = () => {
    // const imagePrompt = formData.prompt || `Create an image about ${formData.topic}`;
    // localStorage.setItem('postPrompt', imagePrompt); // Not needed if passing prompt via prop
    setShowImageModal(true);
  };

  const handleImageAttached = (imageUrl) => {
    setPostImage(imageUrl);
    setShowImageModal(false);
    enqueueSnackbar("Image attached successfully!", {
      variant: "success",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  };

  const handleAttachCarouselModal = () => {
    // localStorage.removeItem('carouselSlides'); // Clear any old slides if necessary
    // localStorage.setItem('carouselGeneratedContent', generatedContent); // Pass content
    setShowCarouselModal(true);
  };

  const handleCarouselAttached = (fileBlob) => {
    setCarouselFile(fileBlob);
    setShowCarouselModal(false);
    enqueueSnackbar("Carousel generated! It will be saved with your draft.", {
      variant: "info",
    });
  };

  // Handler for when user clicks to attach image
  const handleAttachImageClick = () => {
    if (carouselFile) {
      setPendingAttachmentType("image");
      setShowAttachmentConfirmDialog(true);
    } else {
      setShowImageModal(true);
    }
  };

  // Handler for when user clicks to attach carousel
  const handleAttachCarouselClick = () => {
    if (postImage) {
      setPendingAttachmentType("carousel");
      setShowAttachmentConfirmDialog(true);
    } else {
      setShowCarouselModal(true);
    }
  };

  // Handler for attachment confirmation
  const handleAttachmentConfirm = () => {
    if (pendingAttachmentType === "image") {
      setCarouselFile(null);
      setShowImageModal(true);
    } else if (pendingAttachmentType === "carousel") {
      setPostImage("");
      setShowCarouselModal(true);
    }
    setShowAttachmentConfirmDialog(false);
    setPendingAttachmentType(null);
  };

  // Add post modification function
  const handlePostModification = async (
    modificationType,
    instructions = ""
  ) => {
    if (!generatedContent) return;

    setModificationLoading(true);
    setModificationAnchorEl(null); // Close menu

    try {
      const requestData = {
        content: generatedContent,
        modification: modificationType,
        ...(instructions && { customInstructions: instructions }),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/modify-post`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setGeneratedContent(response.data.content);
        toast.success("Post modified successfully!");
        setShowModificationOptions(false);
        setCustomInstructions("");
        setShowCustomInstructionsInput(false);
      }
    } catch (error) {
      console.error("Post modification error:", error);
      toast.error(error.response?.data?.message || "Failed to modify post");
    } finally {
      setModificationLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Generate LinkedIn Post | Instation</title>
        <meta
          name="description"
          content="Create engaging LinkedIn posts with AI-powered content generation."
        />
      </Helmet>

      <WelcomePopup open={showWelcome} onClose={() => setShowWelcome(false)} />
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container">
          {showUpgradeOverlay && (
            <UpgradeOverlay
              message="You've used all your credits for this month"
              buttonText="Upgrade Now"
              onUpgrade={() => navigate("/dashboard/upgrade")}
            />
          )}
          <Grid container spacing={3} className="content-grid">
            {/* Left Panel - Form Section */}
            <Grid item xs={12} md={6} lg={6} className="content-panel">
              <Paper
                elevation={0}
                className="content-paper"
                sx={{
                  // The overflow and scrollbar styling below can remain if you want custom scrollbars for the *new scrollable Box*,
                  // but the Paper itself should not be the primary scroller anymore.
                  // We will move overflow:auto to a child Box.
                  display: "flex", // Make Paper a flex container
                  flexDirection: "column", // Stack children (sticky header, scrollable content)
                  overflow: "hidden", // Prevent Paper itself from scrolling

                  // Custom scrollbar styles can be moved to the scrollable Box later if needed
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#CBD5E0",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#A0AEC0",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#CBD5E0 transparent",
                }}
              >
                <Typography
                  variant="h6"
                  className="component-heading"
                  sx={{
                    mb: 2,
                    fontSize: "1.1rem",
                    fontFamily: "var(--font-family)",
                    flexShrink: 0, // Prevent this from shrinking
                  }}
                >
                  Create LinkedIn Post
                </Typography>

                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    mb: 2, // Consistent margin for Tabs
                    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                    minHeight: "40px", // Reduce tab height
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#4890fe",
                      height: "2px",
                    },
                    "& .MuiTabs-flexContainer": {
                      gap: "4px", // Add small gap between tabs
                    },
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.8rem", // Smaller font size
                      color: "#4A5568",
                      minWidth: "auto", // Allow tabs to be smaller
                      padding: "6px 12px", // Reduce padding
                      minHeight: "40px", // Reduce height
                      "&.Mui-selected": {
                        color: "#4890fe",
                        fontWeight: 600,
                      },
                    },
                    flexShrink: 0, // Prevent Tabs from shrinking
                  }}
                >
                  <Tab value="custom" label="Custom" />
                  <Tab value="templates" label="Templates" />
                  <Tab value="youtube" label="YouTube" />
                  <Tab value="article" label="Article/URL" />
                  <Tab value="image" label="Image" />
                </Tabs>

                {/* New Scrollable Content Area */}
                <Box
                  sx={{
                    flex: 1, // Takes up remaining space
                    overflowY: "auto", // Enable vertical scrolling for this Box only
                    overflowX: "hidden", // Prevent horizontal scroll for this Box
                    pt: 1, // Consistent padding top for scrollable area
                    pr: 2, // Added padding-right to create space from the scrollbar
                    // Custom scrollbar styles are now handled globally by app.css
                  }}
                >
                  {
                    activeTab === "custom" ? (
                      <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Topic field is now the first item here */}
                        <Box sx={{ mb: 2.5 }}>
                          {" "}
                          {/* Ensure consistent bottom margin for Topic */}
                          <Typography
                            variant="subtitle1"
                            className="section-title"
                            style={{
                              fontWeight: 500,
                              color: "black",
                              fontSize: "0.9rem",
                            }}
                            sx={{
                              mb: 0.8,
                            }}
                          >
                            Topic
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={15}
                            name="topic"
                            value={formData.topic}
                            onChange={handleChange}
                            placeholder="What would you like to write about? E.g., Leadership skills, Industry trends, Career advice..."
                            required
                            className="form-field"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "0.95rem",
                                bgcolor: "white",
                                "&.Mui-focused fieldset": {
                                  borderColor: "#4890fe",
                                },
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.1)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "rgba(72, 144, 254, 0.5)",
                                },
                              },
                            }}
                          />
                        </Box>

                        {/* AI Model Selection Dropdown */}
                        <Box sx={{ mb: 2.5 }}>
                          <Typography
                            variant="subtitle1"
                            className="section-title"
                            sx={{
                              mb: 0.8,
                              fontWeight: 500,
                              color: "black",
                              fontSize: "0.9rem",
                            }}
                          >
                            AI Model
                          </Typography>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#4890fe",
                                  },
                              },
                            }}
                          >
                            <Select
                              value={selectedModel}
                              onChange={(e) => setSelectedModel(e.target.value)}
                              sx={{ fontSize: "0.85rem" }}
                            >
                              <MenuItem value="in-model-a">
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    In-Model A
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    Advanced model with full customization
                                    options
                                  </Typography>
                                </Box>
                              </MenuItem>

                              <MenuItem value="in-model-b">
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    In-Model B
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    Creative model with less customization
                                  </Typography>
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Writing Style and Goals in same row */}
                        <Box sx={{ mb: 2.5, display: "flex", gap: 2 }}>
                          {/* Writing Style field - takes up 50% */}
                          <Box sx={{ flex: "1 1 50%" }}>
                            <Typography
                              variant="subtitle1"
                              className="section-title"
                              sx={{
                                mb: 0.8,
                                fontWeight: 500,
                                color: "black",
                                fontSize: "0.9rem",
                              }}
                            >
                              Writing Style
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                name="writingStyle"
                                value={formData.writingStyle || ""}
                                onChange={handleChange}
                                displayEmpty
                                sx={{
                                  borderRadius: "8px",
                                  fontSize: "0.95rem",
                                  bgcolor: "#ffffff",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0, 0, 0, 0.1)",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#4890fe",
                                    },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(72, 144, 254, 0.5)",
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <span style={{ color: "#718096" }}>
                                    Select a writing style (optional)
                                  </span>
                                </MenuItem>
                                <MenuItem value="professional">
                                  Professional
                                </MenuItem>
                                <MenuItem value="casual">Casual</MenuItem>
                                <MenuItem value="funny">Funny</MenuItem>
                                <MenuItem value="educational">
                                  Educational
                                </MenuItem>
                                <MenuItem value="motivational">
                                  Motivational
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Box>

                          {/* Goals field - takes up 50% */}
                          <Box sx={{ flex: "1 1 50%" }}>
                            <Typography
                              variant="subtitle1"
                              className="section-title"
                              sx={{
                                mb: 0.8,
                                fontWeight: 500,
                                color: "black",
                                fontSize: "0.9rem",
                              }}
                            >
                              Goals
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                name="goals"
                                multiple
                                value={formData.goals || []}
                                onChange={handleChange}
                                displayEmpty
                                renderValue={(selected) => {
                                  if (selected.length === 0) {
                                    return (
                                      <span style={{ color: "#718096" }}>
                                        Select goals (optional)
                                      </span>
                                    );
                                  }

                                  // If only one item is selected, show it
                                  if (selected.length === 1) {
                                    return selected[0];
                                  }

                                  // If multiple items are selected, show the first one + count of additional items
                                  return `${selected[0]} +${
                                    selected.length - 1
                                  } more`;
                                }}
                                sx={{
                                  borderRadius: "8px",
                                  fontSize: "0.95rem",
                                  bgcolor: "#ffffff",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0, 0, 0, 0.1)",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#4890fe",
                                    },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(72, 144, 254, 0.5)",
                                  },
                                }}
                              >
                                <MenuItem value="Build a personal brand">
                                  Build a personal brand
                                </MenuItem>
                                <MenuItem value="Generate leads">
                                  Generate leads
                                </MenuItem>
                                <MenuItem value="Increase engagement">
                                  Increase engagement
                                </MenuItem>
                                <MenuItem value="Job change">
                                  Job change
                                </MenuItem>
                                <MenuItem value="Product launch">
                                  Product launch
                                </MenuItem>
                                <MenuItem value="Share industry insights">
                                  Share industry insights
                                </MenuItem>
                                <MenuItem value="Networking">
                                  Networking
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>

                        {/* Personal Tone Analysis and Advanced Settings in same row */}
                        {selectedModel !== "in-model-b" && (
                          <Box sx={{ mb: 2.5, display: "flex", gap: 2 }}>
                            {/* Personal Tone Analysis - takes up 50% */}
                            <Box sx={{ flex: "1 1 50%" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  className="section-title"
                                  sx={{
                                    fontWeight: 500,
                                    color: "black",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  Personal Tone Analysis
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={toneAnalysisEnabled}
                                      onChange={(e) =>
                                        setToneAnalysisEnabled(e.target.checked)
                                      }
                                      size="small"
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "#4890fe",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor: "#4890fe",
                                          },
                                      }}
                                    />
                                  }
                                  label=""
                                  sx={{ ml: "auto", mr: 0 }}
                                />
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  fontSize: "0.8rem",
                                  mb: toneAnalysisEnabled ? 1.2 : 0,
                                }}
                              >
                                {toneAnalysisEnabled
                                  ? "✨ AI will analyze your previous LinkedIn posts."
                                  : "Enable to match your writing style."}
                              </Typography>

                              {toneAnalysisEnabled && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    border: "1px solid rgba(72, 144, 254, 0.2)",
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(72, 144, 254, 0.02)",
                                  }}
                                >
                                  {analyzedTone ? (
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, fontSize: "0.8rem", mb: 0.5 }}
                                      >
                                        Tone analyzed
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 1 }}
                                      >
                                        Style:{" "}
                                        {(() => {
                                          const lowerAnalysis = analyzedTone.toLowerCase();
                                          if (lowerAnalysis.includes("casual") || lowerAnalysis.includes("conversational")) return "Casual and conversational";
                                          if (lowerAnalysis.includes("formal") || lowerAnalysis.includes("professional")) return "Professional and formal";
                                          if (lowerAnalysis.includes("enthusiastic") || lowerAnalysis.includes("energetic")) return "Enthusiastic and energetic";
                                          return "Professional and engaging";
                                        })()}
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => { setAnalyzedTone(""); setManualPostsText(""); }}
                                        sx={{ fontSize: "0.75rem", textTransform: "none", py: 0.25, px: 1 }}
                                      >
                                        Re-analyze
                                      </Button>
                                    </Box>
                                  ) : (
                                    <Box>
                                      <TextField
                                        multiline
                                        minRows={3}
                                        maxRows={6}
                                        fullWidth
                                        size="small"
                                        placeholder="Paste one or more of your LinkedIn posts here"
                                        value={manualPostsText}
                                        onChange={(e) => setManualPostsText(e.target.value)}
                                        sx={{ mb: 1, "& textarea": { fontSize: "0.8rem" } }}
                                      />
                                      <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!manualPostsText.trim() || toneAnalysisLoading}
                                        onClick={analyzeManualTone}
                                        startIcon={toneAnalysisLoading ? <CircularProgress size={12} color="inherit" /> : null}
                                        sx={{
                                          fontSize: "0.75rem",
                                          textTransform: "none",
                                          py: 0.5,
                                          px: 1.5,
                                          bgcolor: "#4890fe",
                                          "&:hover": { bgcolor: "#3a7fe0" },
                                        }}
                                      >
                                        {toneAnalysisLoading ? "Analyzing" : "Analyze My Style"}
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>

                            {/* Advanced Settings - takes up 50% */}
                            <Box sx={{ flex: "1 1 50%" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  className="section-title"
                                  sx={{
                                    fontWeight: 500,
                                    color: "black",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  Advanced Settings
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={advancedSettingsEnabled}
                                      onChange={(e) =>
                                        setAdvancedSettingsEnabled(
                                          e.target.checked
                                        )
                                      }
                                      size="small"
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "#4890fe",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor: "#4890fe",
                                          },
                                      }}
                                    />
                                  }
                                  label=""
                                  sx={{ ml: "auto", mr: 0 }}
                                />
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  fontSize: "0.8rem",
                                  mb: advancedSettingsEnabled ? 1.2 : 0,
                                }}
                              >
                                {advancedSettingsEnabled
                                  ? "✨ Customize industry, role, and audience."
                                  : "Enable advanced personalization options."}
                              </Typography>

                              {advancedSettingsEnabled && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    border: "1px solid rgba(72, 144, 254, 0.2)",
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(72, 144, 254, 0.02)",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      mb: 1.5,
                                      fontSize: "0.8rem",
                                      color: "#6B7280",
                                    }}
                                  >
                                    Personalize content for your industry and
                                    role.
                                  </Typography>

                                  <Box sx={{ mb: 1.5 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mb: 0.5,
                                        fontWeight: 500,
                                        color: "black",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      Industry
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      name="industry"
                                      value={formData.industry}
                                      onChange={handleChange}
                                      placeholder="e.g. Technology, Finance"
                                      size="small"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                          fontSize: "0.8rem",
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#4890fe",
                                          },
                                          "& fieldset": {
                                            borderColor: "rgba(0, 0, 0, 0.1)",
                                          },
                                        },
                                      }}
                                    />
                                  </Box>

                                  <Box sx={{ mb: 1.5 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mb: 0.5,
                                        fontWeight: 500,
                                        color: "black",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      Role
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      name="professionalRole"
                                      value={formData.professionalRole}
                                      onChange={handleChange}
                                      placeholder="e.g. Marketing Manager"
                                      size="small"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                          fontSize: "0.8rem",
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#4890fe",
                                          },
                                          "& fieldset": {
                                            borderColor: "rgba(0, 0, 0, 0.1)",
                                          },
                                        },
                                      }}
                                    />
                                  </Box>

                                  <Box sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mb: 0.5,
                                        fontWeight: 500,
                                        color: "black",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      Target Audience
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      name="targetAudience"
                                      value={formData.targetAudience}
                                      onChange={handleChange}
                                      placeholder="e.g. Entrepreneurs"
                                      size="small"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                          fontSize: "0.8rem",
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#4890fe",
                                          },
                                          "& fieldset": {
                                            borderColor: "rgba(0, 0, 0, 0.1)",
                                          },
                                        },
                                      }}
                                    />
                                  </Box>

                                  <Button
                                    variant="outlined"
                                    onClick={handleSaveProfessionalDetails}
                                    size="small"
                                    startIcon={
                                      profDetailsLoading ? (
                                        <CircularProgress size={12} />
                                      ) : null
                                    }
                                    disabled={profDetailsLoading}
                                    sx={{
                                      borderRadius: "4px",
                                      textTransform: "none",
                                      fontWeight: 400,
                                      fontSize: "0.75rem",
                                      borderColor: "#4890fe",
                                      color: "#4890fe",
                                      py: 0.25,
                                      px: 1,
                                      minHeight: "auto",
                                      "&:hover": {
                                        borderColor: "#3a7ad9",
                                        backgroundColor:
                                          "rgba(72, 144, 254, 0.05)",
                                      },
                                    }}
                                  >
                                    {profDetailsLoading
                                      ? "Saving..."
                                      : "Save to Profile"}
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Content Preferences */}
                        {selectedModel !== "in-model-b" && (
                          <Box sx={{ mb: 2.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                className="section-title"
                                sx={{
                                  fontWeight: 500,
                                  color: "black",
                                  fontSize: "0.9rem",
                                }}
                              >
                                Content Preferences
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={contentPreferencesEnabled}
                                    onChange={(e) =>
                                      setContentPreferencesEnabled(
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#4890fe",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "#4890fe",
                                        },
                                    }}
                                  />
                                }
                                label=""
                                sx={{ ml: "auto", mr: 0 }}
                              />
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6B7280",
                                fontSize: "0.8rem",
                                mb: contentPreferencesEnabled ? 1.2 : 0,
                              }}
                            >
                              {contentPreferencesEnabled
                                ? "✨ Customize content length, emojis, and hashtags for your posts."
                                : "Enable to customize content formatting preferences."}
                            </Typography>

                            {contentPreferencesEnabled && (
                              <Box
                                sx={{
                                  p: 2,
                                  border: "1px solid rgba(72, 144, 254, 0.2)",
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(72, 144, 254, 0.02)",
                                }}
                              >
                                {/* Length Selection */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: "#4A5568",
                                      fontSize: "0.85rem",
                                      mb: 1,
                                    }}
                                  >
                                    Content Length
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                    {[
                                      { value: "default", label: "Default" },
                                      { value: "short", label: "Short" },
                                      { value: "medium", label: "Medium" },
                                      { value: "long", label: "Long" },
                                    ].map((option) => (
                                      <Box
                                        key={option.value}
                                        onClick={() =>
                                          setContentLength(option.value)
                                        }
                                        sx={{
                                          flex: 1,
                                          py: 0.75,
                                          px: 1,
                                          border:
                                            contentLength === option.value
                                              ? "1.5px solid #4890fe"
                                              : "1px solid rgba(0, 0, 0, 0.12)",
                                          borderRadius: "6px",
                                          textAlign: "center",
                                          cursor: "pointer",
                                          backgroundColor:
                                            contentLength === option.value
                                              ? "rgba(72, 144, 254, 0.08)"
                                              : "white",
                                          transition: "all 0.15s ease",
                                          "&:hover": {
                                            borderColor: "#4890fe",
                                            backgroundColor:
                                              "rgba(72, 144, 254, 0.04)",
                                          },
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight:
                                              contentLength === option.value
                                                ? 600
                                                : 500,
                                            color:
                                              contentLength === option.value
                                                ? "#4890fe"
                                                : "#5A6C7D",
                                            fontSize: "0.8rem",
                                          }}
                                        >
                                          {option.label}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>

                                {/* Emojis and Hashtags Button Selection */}
                                <Box sx={{ display: "flex", gap: 2 }}>
                                  {/* Emojis */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: "#4A5568",
                                        fontSize: "0.85rem",
                                        mb: 1,
                                      }}
                                    >
                                      Emojis
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                      {[
                                        {
                                          value: false,
                                          label: "None",
                                          icon: "🚫",
                                        },
                                        {
                                          value: true,
                                          label: "Include",
                                          icon: "😊",
                                        },
                                      ].map((option) => (
                                        <Box
                                          key={option.value.toString()}
                                          onClick={() =>
                                            setIncludeEmojis(option.value)
                                          }
                                          sx={{
                                            flex: 1,
                                            py: 0.75,
                                            px: 1,
                                            border:
                                              includeEmojis === option.value
                                                ? "1.5px solid #4890fe"
                                                : "1px solid rgba(0, 0, 0, 0.12)",
                                            borderRadius: "6px",
                                            textAlign: "center",
                                            cursor: "pointer",
                                            backgroundColor:
                                              includeEmojis === option.value
                                                ? "rgba(72, 144, 254, 0.08)"
                                                : "white",
                                            transition: "all 0.15s ease",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                            "&:hover": {
                                              borderColor: "#4890fe",
                                              backgroundColor:
                                                "rgba(72, 144, 254, 0.04)",
                                            },
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.7rem",
                                            }}
                                          >
                                            {option.icon}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight:
                                                includeEmojis === option.value
                                                  ? 600
                                                  : 500,
                                              color:
                                                includeEmojis === option.value
                                                  ? "#4890fe"
                                                  : "#5A6C7D",
                                              fontSize: "0.8rem",
                                            }}
                                          >
                                            {option.label}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>

                                  {/* Hashtags */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: "#4A5568",
                                        fontSize: "0.85rem",
                                        mb: 1,
                                      }}
                                    >
                                      Hashtags
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                      {[
                                        {
                                          value: false,
                                          label: "None",
                                          icon: "🚫",
                                        },
                                        {
                                          value: true,
                                          label: "Include",
                                          icon: "#",
                                        },
                                      ].map((option) => (
                                        <Box
                                          key={option.value.toString()}
                                          onClick={() =>
                                            setIncludeHashtags(option.value)
                                          }
                                          sx={{
                                            flex: 1,
                                            py: 0.75,
                                            px: 1,
                                            border:
                                              includeHashtags === option.value
                                                ? "1.5px solid #4890fe"
                                                : "1px solid rgba(0, 0, 0, 0.12)",
                                            borderRadius: "6px",
                                            textAlign: "center",
                                            cursor: "pointer",
                                            backgroundColor:
                                              includeHashtags === option.value
                                                ? "rgba(72, 144, 254, 0.08)"
                                                : "white",
                                            transition: "all 0.15s ease",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                            "&:hover": {
                                              borderColor: "#4890fe",
                                              backgroundColor:
                                                "rgba(72, 144, 254, 0.04)",
                                            },
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.7rem",
                                              fontWeight: option.value
                                                ? 500
                                                : 400,
                                            }}
                                          >
                                            {option.icon}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight:
                                                includeHashtags === option.value
                                                  ? 600
                                                  : 500,
                                              color:
                                                includeHashtags === option.value
                                                  ? "#4890fe"
                                                  : "#5A6C7D",
                                              fontSize: "0.8rem",
                                            }}
                                          >
                                            {option.label}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Generate Button */}
                        <Box sx={{ mb: 1.5, mt: 1 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="medium"
                            fullWidth
                            className="primary-button"
                            disabled={
                              loading ||
                              !formData.topic ||
                              (toneAnalysisEnabled && toneAnalysisLoading)
                            }
                            sx={{
                              bgcolor: "#4890fe",
                              borderRadius: "6px",
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: "0.9rem",
                              px: 2,
                              py: 1,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: "#3a7ad9",
                                boxShadow: "none",
                              },
                            }}
                            startIcon={
                              loading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <SendIcon fontSize="small" />
                              )
                            }
                          >
                            {loading ? (
                              "Generating..."
                            ) : toneAnalysisEnabled && toneAnalysisLoading ? (
                              "Analyzing your tone..."
                            ) : (
                              <>
                                Generate Post
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    fontSize: "0.75rem",
                                    color: !formData.topic
                                      ? "rgba(255, 255, 255, 0.5)"
                                      : "white",
                                  }}
                                >
                                  (1 credit)
                                </Typography>
                              </>
                            )}
                          </Button>
                        </Box>

                        {/* Options Showcase - This entire section will be removed */}
                        {/* <Divider sx={{ my: 2 }} /> ... up to ... <Divider sx={{ my: 2 }} /> */}
                        {/* End Options Showcase */}
                      </Box>
                    ) : activeTab === "templates" ? (
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {/* Template Category Selection */}
                        <FormControl
                          fullWidth
                          sx={{
                            mb: 2,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#4890fe",
                                },
                            },
                          }}
                          size="small"
                        >
                          <InputLabel sx={{ fontSize: "0.9rem" }}>
                            Template Category
                          </InputLabel>
                          <Select
                            value={templateCategory}
                            onChange={(e) =>
                              setTemplateCategory(e.target.value)
                            }
                            label="Template Category"
                            sx={{ fontSize: "0.9rem" }}
                          >
                            <MenuItem value="viral" sx={{ fontSize: "0.9rem" }}>
                              Viral Templates ({templates.viral.length})
                            </MenuItem>
                            <MenuItem
                              value="professional"
                              disabled
                              sx={{ fontSize: "0.9rem" }}
                            >
                              Professional Templates (Coming soon)
                            </MenuItem>
                            <MenuItem
                              value="engagement"
                              disabled
                              sx={{ fontSize: "0.9rem" }}
                            >
                              Engagement Templates (Coming soon)
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {/* Template Topic input - moved to top when template is selected */}
                        {selectedTemplate && (
                          <Box ref={templateTopicRef} sx={{ mb: 3 }}>
                            <Typography
                              variant="body1"
                              gutterBottom
                              sx={{
                                fontWeight: 500,
                                color: "#4A5568",
                                fontSize: "0.95rem",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                              }}
                              onClick={() => {
                                setFullTemplateTitle(selectedTemplate.title);
                                setFullTemplateText(selectedTemplate.template);
                                setOpenTemplateDialog(true);
                              }}
                            >
                              Selected template:{" "}
                              <span
                                style={{
                                  color: "#4890fe",
                                  marginLeft: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {selectedTemplate.title}
                                <VisibilityIcon
                                  fontSize="small"
                                  sx={{ ml: 0.5, fontSize: "16px" }}
                                />
                              </span>
                            </Typography>
                            <TextField
                              fullWidth
                              label="Enter your topic"
                              variant="outlined"
                              value={templateTopic}
                              onChange={(e) => setTemplateTopic(e.target.value)}
                              sx={{
                                mt: 1,
                                mb: 2,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  fontSize: "0.95rem",
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#4890fe",
                                  },
                                  "& fieldset": {
                                    borderColor: "rgba(0, 0, 0, 0.1)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "rgba(72, 144, 254, 0.5)",
                                  },
                                },
                              }}
                              size="small"
                              placeholder="e.g., Leadership, Remote Work, Customer Success"
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleGenerateFromTemplateLocal}
                              disabled={templateLoading || !templateTopic}
                              startIcon={
                                templateLoading ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : null
                              }
                              sx={{
                                backgroundColor: "#4890fe",
                                color: "white",
                                borderRadius: "6px",
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "0.9rem",
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundColor: "#3a7ad9",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {templateLoading ? (
                                "Generating..."
                              ) : (
                                <>
                                  Generate from Template
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      fontSize: "0.75rem",
                                      color: !templateTopic
                                        ? "rgba(255, 255, 255, 0.5)"
                                        : "white",
                                    }}
                                  >
                                    (1 credit)
                                  </Typography>
                                </>
                              )}
                            </Button>
                          </Box>
                        )}

                        {/* Template Grid - ensure this Box does not scroll independently */}
                        <Box
                          sx={{
                            maxHeight: selectedTemplate ? "400px" : "550px",
                            overflow: "auto",
                            pr: 1,
                          }}
                        >
                          <Grid container spacing={2}>
                            {templates[templateCategory]?.map(
                              (template, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                  <Card
                                    className="content-card"
                                    sx={{
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      borderRadius: 2,
                                      boxShadow: "none",
                                      border:
                                        selectedTemplate?.id === template.id
                                          ? "2px solid #4890fe"
                                          : "1px solid rgba(0, 0, 0, 0.08)",
                                      transition: "all 0.2s ease-in-out",
                                      "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow:
                                          "0 10px 20px rgba(0, 0, 0, 0.08)",
                                        borderColor: "#4890fe",
                                      },
                                      backgroundColor:
                                        selectedTemplate?.id === template.id
                                          ? "rgba(72, 144, 254, 0.05)"
                                          : "white",
                                    }}
                                  >
                                    <CardActionArea
                                      sx={{ height: "100%", p: 1 }}
                                      onClick={() =>
                                        handleTemplateClickLocal(template)
                                      }
                                    >
                                      <CardContent sx={{ p: 1.5 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            mb: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            component="div"
                                            gutterBottom
                                            sx={{
                                              fontWeight: 600,
                                              color:
                                                selectedTemplate?.id ===
                                                template.id
                                                  ? "#4890fe"
                                                  : "#2D3748",
                                              fontSize: "0.95rem",
                                              mb: 0,
                                            }}
                                          >
                                            {template.title}
                                          </Typography>
                                          <Tooltip title="View full template">
                                            <IconButton
                                              size="small"
                                              onClick={(e) =>
                                                handleViewFullTemplateLocal(
                                                  e,
                                                  template
                                                )
                                              }
                                              sx={{
                                                p: 0.5,
                                                color: "#718096",
                                                "&:hover": {
                                                  color: "#4890fe",
                                                  backgroundColor:
                                                    "rgba(72, 144, 254, 0.08)",
                                                },
                                              }}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            height: "80px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 4,
                                            WebkitBoxOrient: "vertical",
                                            fontSize: "0.8rem",
                                            color: "#718096",
                                          }}
                                        >
                                          {template.template}
                                        </Typography>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                </Grid>
                              )
                            )}
                          </Grid>
                        </Box>
                      </Box>
                    ) : activeTab === "youtube" ? (
                      // Ensure this Box correctly renders YouTube specific UI
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          variant="subtitle1"
                          className="section-title"
                          style={{ fontWeight: 500, color: "black" }}
                          sx={{ mb: 1 }}
                        >
                          Enter YouTube Video URL
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                          For best results, use videos that are 15 minutes or
                          less with English subtitles
                        </Typography>

                        <Box
                          component="form"
                          onSubmit={handleYoutubeSubmit} // Ensure this handler is correctly defined and used
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <TextField
                            fullWidth
                            value={youtubeUrl}
                            onChange={(e) => setYoutubeUrl(e.target.value)}
                            placeholder="e.g., https://www.youtube.com/watch?v=YSrVG74Emyk"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "0.95rem",
                                "&.Mui-focused fieldset": {
                                  borderColor: "#4890fe",
                                },
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.1)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "rgba(72, 144, 254, 0.5)",
                                },
                              },
                            }}
                          />

                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={youtubeLoading || loading || !youtubeUrl}
                            startIcon={
                              youtubeLoading || loading ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <YouTubeIcon />
                              )
                            }
                            sx={{
                              backgroundColor: "#FF0000",
                              color: "white",
                              borderRadius: "6px",
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: "0.9rem",
                              boxShadow: "none",
                              "&:hover": {
                                backgroundColor: "#CC0000",
                                boxShadow: "none",
                              },
                              alignSelf: "flex-start",
                              px: 3,
                            }}
                          >
                            {youtubeLoading || loading ? (
                              "Generating Post..."
                            ) : (
                              <>
                                Generate Post from YouTube
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    fontSize: "0.75rem",
                                    color: !youtubeUrl
                                      ? "rgba(255, 255, 255, 0.5)"
                                      : "white",
                                  }}
                                >
                                  (1 credit)
                                </Typography>
                              </>
                            )}
                          </Button>
                        </Box>

                        {youtubeError && (
                          <Alert
                            severity="error"
                            sx={{
                              mt: 2,
                              borderRadius: "8px",
                            }}
                          >
                            {youtubeError}
                          </Alert>
                        )}

                        {(youtubeLoading || loading) && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              mt: 4,
                            }}
                          >
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                          </Box>
                        )}
                      </Box>
                    ) : activeTab === "article" ? (
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          variant="subtitle1"
                          className="section-title"
                          sx={{
                            mb: 2,
                            fontWeight: 500,
                            color: "black",
                          }}
                        >
                          Enter Article URL
                        </Typography>

                        <Box
                          component="form"
                          onSubmit={handleArticleSubmitLocal}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <TextField
                            fullWidth
                            value={articleUrl}
                            onChange={(e) => setArticleUrl(e.target.value)}
                            placeholder="e.g., https://example.com/article"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "0.95rem",
                                "&.Mui-focused fieldset": {
                                  borderColor: "#4890fe",
                                },
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.1)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "rgba(72, 144, 254, 0.5)",
                                },
                              },
                            }}
                          />

                          <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={useAdvancedSettings}
                                  onChange={(e) =>
                                    setUseAdvancedSettings(e.target.checked)
                                  }
                                  color="primary"
                                />
                              }
                              label="Use Advanced Settings"
                              sx={{ mb: 2 }}
                            />

                            {useAdvancedSettings && (
                              <Box
                                sx={{
                                  p: 2,
                                  border: "1px solid rgba(0, 0, 0, 0.1)",
                                  borderRadius: "8px",
                                  bgcolor: "#ffffff",
                                }}
                              >
                                {/* Writing Style field */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    className="section-title"
                                    sx={{
                                      mb: 1,
                                      fontWeight: 500,
                                      color: "black",
                                    }}
                                  >
                                    Writing Style
                                  </Typography>
                                  <FormControl fullWidth size="small">
                                    <Select
                                      name="writingStyle"
                                      value={formData.writingStyle || ""}
                                      onChange={handleChange}
                                      displayEmpty
                                      sx={{
                                        borderRadius: "8px",
                                        fontSize: "0.95rem",
                                        bgcolor: "#ffffff",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "rgba(0, 0, 0, 0.1)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#4890fe",
                                          },
                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor:
                                              "rgba(72, 144, 254, 0.5)",
                                          },
                                      }}
                                    >
                                      <MenuItem value="">
                                        <span style={{ color: "#718096" }}>
                                          Select a writing style (optional)
                                        </span>
                                      </MenuItem>
                                      <MenuItem value="professional">
                                        Professional
                                      </MenuItem>
                                      <MenuItem value="casual">Casual</MenuItem>
                                      <MenuItem value="funny">Funny</MenuItem>
                                      <MenuItem value="educational">
                                        Educational
                                      </MenuItem>
                                      <MenuItem value="motivational">
                                        Motivational
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>

                                {/* Goals field */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    className="section-title"
                                    sx={{
                                      mb: 1,
                                      fontWeight: 500,
                                      color: "black",
                                    }}
                                  >
                                    Goals
                                  </Typography>
                                  <FormControl fullWidth size="small">
                                    <Select
                                      name="goals"
                                      multiple
                                      value={formData.goals || []}
                                      onChange={handleChange}
                                      displayEmpty
                                      renderValue={(selected) => {
                                        if (selected.length === 0) {
                                          return (
                                            <span style={{ color: "#718096" }}>
                                              Select goals (optional)
                                            </span>
                                          );
                                        }
                                        return `${selected[0]} +${
                                          selected.length - 1
                                        } more`;
                                      }}
                                      sx={{
                                        borderRadius: "8px",
                                        fontSize: "0.95rem",
                                        bgcolor: "#ffffff",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "rgba(0, 0, 0, 0.1)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#4890fe",
                                          },
                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor:
                                              "rgba(72, 144, 254, 0.5)",
                                          },
                                      }}
                                    >
                                      <MenuItem value="Build a personal brand">
                                        Build a personal brand
                                      </MenuItem>
                                      <MenuItem value="Generate leads">
                                        Generate leads
                                      </MenuItem>
                                      <MenuItem value="Increase engagement">
                                        Increase engagement
                                      </MenuItem>
                                      <MenuItem value="Job change">
                                        Job change
                                      </MenuItem>
                                      <MenuItem value="Product launch">
                                        Product launch
                                      </MenuItem>
                                      <MenuItem value="Share industry insights">
                                        Share industry insights
                                      </MenuItem>
                                      <MenuItem value="Networking">
                                        Networking
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>

                                {/* Industry field */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    className="section-title"
                                    sx={{
                                      mb: 1,
                                      fontWeight: 500,
                                      color: "black",
                                    }}
                                  >
                                    Industry
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    name="industry"
                                    value={formData.industry}
                                    onChange={handleChange}
                                    placeholder="e.g. Technology, Finance, Healthcare"
                                    size="small"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontSize: "0.9rem",
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#4890fe",
                                        },
                                        "& fieldset": {
                                          borderColor: "rgba(0, 0, 0, 0.1)",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            "rgba(72, 144, 254, 0.5)",
                                        },
                                      },
                                    }}
                                  />
                                </Box>

                                {/* Professional Role field */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    className="section-title"
                                    sx={{
                                      mb: 1,
                                      fontWeight: 500,
                                      color: "black",
                                    }}
                                  >
                                    Professional Role
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    name="professionalRole"
                                    value={formData.professionalRole}
                                    onChange={handleChange}
                                    placeholder="e.g. Marketing Manager, Software Engineer"
                                    size="small"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontSize: "0.9rem",
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#4890fe",
                                        },
                                        "& fieldset": {
                                          borderColor: "rgba(0, 0, 0, 0.1)",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            "rgba(72, 144, 254, 0.5)",
                                        },
                                      },
                                    }}
                                  />
                                </Box>

                                {/* Target Audience field */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    className="section-title"
                                    sx={{
                                      mb: 1,
                                      fontWeight: 500,
                                      color: "black",
                                    }}
                                  >
                                    Target Audience
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    name="targetAudience"
                                    value={formData.targetAudience}
                                    onChange={handleChange}
                                    placeholder="e.g. Entrepreneurs, Job Seekers, Marketers"
                                    size="small"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontSize: "0.9rem",
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#4890fe",
                                        },
                                        "& fieldset": {
                                          borderColor: "rgba(0, 0, 0, 0.1)",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            "rgba(72, 144, 254, 0.5)",
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>

                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={articleLoading || loading || !articleUrl}
                            startIcon={
                              articleLoading || loading ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <MagicIcon />
                              )
                            }
                            sx={{
                              backgroundColor: "#4890fe",
                              color: "white",
                              borderRadius: "6px",
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: "0.9rem",
                              boxShadow: "none",
                              "&:hover": {
                                backgroundColor: "#3a7ad9",
                                boxShadow: "none",
                              },
                              alignSelf: "flex-start",
                              px: 3,
                            }}
                          >
                            {articleLoading || loading
                              ? "Analyzing Article..."
                              : "Analyze Article"}
                            {/* This button's text changes based on state. We only add credit if it's for actual generation. */}
                            {/* For now, let's assume 'Analyze Article' does not cost, but 'Generate Post from Article' does. */}
                            {/* The actual generation button is below after content is shown. */}
                          </Button>
                        </Box>

                        {articleError && (
                          <Alert
                            severity="error"
                            sx={{
                              mt: 2,
                              borderRadius: "8px",
                            }}
                          >
                            {articleError}
                          </Alert>
                        )}

                        {showArticlePreview && articleContent && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                mb: 2,
                                fontWeight: 500,
                                color: "#2D3748",
                              }}
                            >
                              Article Content
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                border: "1px solid rgba(0, 0, 0, 0.1)",
                                borderRadius: "8px",
                                maxHeight: "300px",
                                overflow: "auto",
                                bgcolor: "white",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  lineHeight: 1.6,
                                  color: "black",
                                }}
                              >
                                {articleContent}
                              </Typography>
                            </Paper>

                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleGenerateFromArticleLocal}
                              disabled={loading}
                              startIcon={
                                loading ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <MagicIcon />
                                )
                              }
                              sx={{
                                mt: 2,
                                backgroundColor: "#4890fe",
                                color: "white",
                                borderRadius: "6px",
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "0.9rem",
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundColor: "#3a7ad9",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {loading
                                ? "Generating Post..."
                                : "Generate Post from Article"}
                              {!loading && (
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    color: "white",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  (1 credit)
                                </Typography>
                              )}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ) : activeTab === "image" ? (
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          variant="subtitle1"
                          className="section-title"
                          sx={{
                            mb: 2,
                            fontWeight: 500,
                            color: "black",
                          }}
                        >
                          Upload Image
                        </Typography>

                        <Box
                          sx={{
                            border: "2px dashed rgba(0, 0, 0, 0.1)",
                            borderRadius: "8px",
                            p: 3,
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: "#4890fe",
                              backgroundColor: "rgba(72, 144, 254, 0.05)",
                            },
                          }}
                        >
                          <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="upload-image-button"
                            type="file"
                            onChange={handleImageUploadEvent}
                            disabled={topicLoading}
                          />
                          <label
                            htmlFor="upload-image-button"
                            style={{ width: "100%", cursor: "pointer" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              {topicLoading ? (
                                <CircularProgress size={40} sx={{ mb: 2 }} />
                              ) : (
                                <ImageIcon
                                  sx={{ fontSize: 40, color: "#4890fe", mb: 2 }}
                                />
                              )}
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#2D3748",
                                  fontWeight: 500,
                                  mb: 1,
                                }}
                              >
                                {topicLoading
                                  ? "Analyzing Image..."
                                  : "Click to upload or drag and drop"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#718096",
                                  fontSize: "0.875rem",
                                }}
                              >
                                Supported formats: JPG, PNG, GIF
                              </Typography>
                            </Box>
                          </label>
                        </Box>

                        {imageGeneratedTopic && ( // Check imageGeneratedTopic instead of formData.topic
                          <Box sx={{ mt: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 500,
                                  color: "#2D3748",
                                }}
                              >
                                Generated Topic
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => setIsEditing(true)}
                                sx={{
                                  borderColor: "#4890fe",
                                  color: "#4890fe",
                                  borderRadius: "6px",
                                  textTransform: "none",
                                  fontWeight: 500,
                                  "&:hover": {
                                    borderColor: "#3a7ad9",
                                    bgcolor: "rgba(72, 144, 254, 0.04)",
                                  },
                                }}
                              >
                                Edit
                              </Button>
                            </Box>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                border: "1px solid rgba(0, 0, 0, 0.1)",
                                borderRadius: "8px",
                                bgcolor: "white",
                              }}
                            >
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  value={editedTopic}
                                  onChange={(e) =>
                                    setEditedTopic(e.target.value)
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      fontSize: "0.95rem",
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#4890fe",
                                      },
                                      "& fieldset": {
                                        borderColor: "rgba(0, 0, 0, 0.1)",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: "rgba(72, 144, 254, 0.5)",
                                      },
                                    },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: 1.6,
                                    color: "black",
                                  }}
                                >
                                  {/* Display editedTopic if editing, otherwise imageGeneratedTopic */}
                                  {isEditing
                                    ? editedTopic
                                    : imageGeneratedTopic}
                                </Typography>
                              )}
                            </Paper>

                            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                              {isEditing ? (
                                <>
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      setIsEditing(false);
                                      setEditedTopic(imageGeneratedTopic); // Reset to original imageGeneratedTopic
                                    }}
                                    sx={{
                                      borderColor: "#718096",
                                      color: "#718096",
                                      borderRadius: "6px",
                                      textTransform: "none",
                                      fontWeight: 500,
                                      "&:hover": {
                                        borderColor: "#4A5568",
                                        bgcolor: "rgba(74, 85, 104, 0.04)",
                                      },
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    onClick={() => {
                                      // setFormData(prev => ({ // Don't update formData.topic here on save
                                      //   ...prev,
                                      //   topic: editedTopic
                                      // }));
                                      setImageGeneratedTopic(editedTopic); // Save edited topic to imageGeneratedTopic
                                      setIsEditing(false);
                                    }}
                                    sx={{
                                      bgcolor: "#4890fe",
                                      borderRadius: "6px",
                                      textTransform: "none",
                                      fontWeight: 500,
                                      fontSize: "0.9rem",
                                      boxShadow: "none",
                                      "&:hover": {
                                        bgcolor: "#3a7ad9",
                                        boxShadow: "none",
                                      },
                                    }}
                                  >
                                    Save Changes
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleGenerateWrapper}
                                  disabled={loading}
                                  startIcon={
                                    loading ? (
                                      <CircularProgress
                                        size={20}
                                        color="inherit"
                                      />
                                    ) : (
                                      <MagicIcon />
                                    )
                                  }
                                  sx={{
                                    backgroundColor: "#4890fe",
                                    color: "white",
                                    borderRadius: "6px",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    fontSize: "0.9rem",
                                    boxShadow: "none",
                                    "&:hover": {
                                      backgroundColor: "#3a7ad9",
                                      boxShadow: "none",
                                    },
                                  }}
                                >
                                  {loading
                                    ? "Generating Post..."
                                    : "Generate Post from Image"}
                                  {!loading && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        color: "white",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      (1 credit)
                                    </Typography>
                                  )}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ) : null /* No default/fallback content, or an explicit empty Box if preferred */
                  }
                </Box>
              </Paper>
            </Grid>

            {/* Right Panel - Preview Section */}
            <Grid item xs={12} md={6} lg={6} className="content-panel">
              <Paper elevation={0} className="content-paper">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                    pb: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#2D3748",
                        fontSize: "1.1rem",
                        fontFamily: "var(--font-family)",
                      }}
                    >
                      Preview
                    </Typography>
                    <ContentScorer
                      content={editedContent || generatedContent}
                      hasImage={!!postImage || !!carouselFile}
                      hashtags={generatedHashtags}
                      isVisible={true}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 0.5, sm: 1 }, // Keep reduced gap
                      mt: 0,
                      justifyContent: "flex-end",
                      flexWrap: "wrap", // Allow wrapping to prevent horizontal scrollbar
                    }}
                  >
                    {/* Copy Button */}
                    <Tooltip title="Copy to clipboard">
                      <Button
                        variant="outlined"
                        onClick={handleCopy}
                        size="small"
                        disabled={copySuccess || !generatedContent}
                        sx={{
                          minWidth: { xs: "40px", sm: "40px", md: "auto" },
                          width: "auto",
                          height: { xs: "36px", sm: "36px", md: "40px" },
                          px: { xs: 1, sm: 1, md: 1.5 },
                          borderRadius: "8px",
                          borderColor: "#4890fe",
                          color: "#4890fe",
                          fontSize: { md: "0.8rem" }, // Font size for md and up
                          "&:hover": {
                            borderColor: "#3a7ad9",
                            bgcolor: "rgba(72, 144, 254, 0.04)",
                          },
                        }}
                      >
                        {copySuccess ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          <>
                            <ContentCopyIcon
                              fontSize="small"
                              sx={{ mr: { xs: 0, sm: 0, md: 0.5 } }}
                            />
                            <Box
                              component="span"
                              sx={{
                                display: {
                                  xs: "none",
                                  sm: "none",
                                  md: "inline",
                                },
                              }}
                            >
                              Copy
                            </Box>
                          </>
                        )}
                      </Button>
                    </Tooltip>

                    {/* Save Button */}
                    <Tooltip title="Save Draft">
                      <Button
                        variant="outlined"
                        onClick={handleSavePost}
                        size="small"
                        disabled={saveLoading || !generatedContent}
                        sx={{
                          minWidth: { xs: "40px", sm: "40px", md: "auto" },
                          width: "auto",
                          height: { xs: "36px", sm: "36px", md: "40px" },
                          px: { xs: 1, sm: 1, md: 1.5 },
                          borderRadius: "8px",
                          borderColor: "#28a745",
                          color: "#28a745",
                          fontSize: { md: "0.8rem" }, // Font size for md and up
                          "&:hover": {
                            borderColor: "#218838",
                            bgcolor: "rgba(40, 167, 69, 0.1)",
                          },
                        }}
                      >
                        {saveLoading ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: "#28a745" }}
                          />
                        ) : (
                          <>
                            <SaveIcon
                              fontSize="small"
                              sx={{ mr: { xs: 0, sm: 0, md: 0.5 } }}
                            />
                            <Box
                              component="span"
                              sx={{
                                display: {
                                  xs: "none",
                                  sm: "none",
                                  md: "inline",
                                },
                              }}
                            >
                              Save
                            </Box>
                          </>
                        )}
                      </Button>
                    </Tooltip>

                    {/* Attach Image Button */}
                    <Tooltip title="Attach an image to this post">
                      <Button
                        variant="outlined"
                        onClick={handleAttachImageClick}
                        size="small"
                        disabled={!generatedContent}
                        sx={{
                          minWidth: { xs: "40px", sm: "40px", md: "auto" },
                          width: "auto",
                          height: { xs: "36px", sm: "36px", md: "40px" },
                          px: { xs: 1, sm: 1, md: 1.5 },
                          borderRadius: "8px",
                          borderColor: "#4890fe",
                          color: "#4890fe",
                          fontSize: { md: "0.8rem" }, // Font size for md and up
                          "&:hover": {
                            borderColor: "#3a7ad9",
                            bgcolor: "rgba(72, 144, 254, 0.04)",
                          },
                          "&.Mui-disabled": {
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            color: "rgba(0, 0, 0, 0.26)",
                          },
                        }}
                      >
                        <AddPhotoAlternateIcon
                          fontSize="small"
                          sx={{ mr: { xs: 0, sm: 0, md: 0.5 } }}
                        />
                        <Box
                          component="span"
                          sx={{
                            display: { xs: "none", sm: "none", md: "inline" },
                          }}
                        >
                          Image
                        </Box>
                      </Button>
                    </Tooltip>

                    {/* Attach Carousel Button */}
                    <Tooltip title="Attach a carousel to this post (2 credits for generation)">
                      <Button
                        variant="outlined"
                        onClick={handleAttachCarouselClick}
                        size="small"
                        disabled={!generatedContent || carouselLoading}
                        sx={{
                          minWidth: { xs: "40px", sm: "40px", md: "auto" },
                          width: "auto",
                          height: { xs: "36px", sm: "36px", md: "40px" },
                          px: { xs: 1, sm: 1, md: 1.5 },
                          borderRadius: "8px",
                          borderColor: "#9c27b0",
                          color: "#9c27b0",
                          fontSize: { md: "0.8rem" }, // Font size for md and up
                          "&:hover": {
                            borderColor: "#7b1fa2",
                            bgcolor: "rgba(156, 39, 176, 0.04)",
                          },
                        }}
                      >
                        {carouselLoading ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: "#9c27b0" }}
                          />
                        ) : (
                          <>
                            <AttachFileIcon
                              fontSize="small"
                              sx={{ mr: { xs: 0, sm: 0, md: 0.5 } }}
                            />
                            <Box
                              component="span"
                              sx={{
                                display: {
                                  xs: "none",
                                  sm: "none",
                                  md: "inline",
                                },
                              }}
                            >
                              Carousel
                            </Box>
                          </>
                        )}
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    flex: 1, // Changed from flexGrow: 1 for consistency, ensures it takes available space
                    overflow: "auto", // This Box should also manage its overflow if its content is too large
                    // minHeight: '500px', // Removed this line
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    bgcolor: "#ffffff",
                    position: "relative", // Keep for absolute positioning of loading/empty states
                    display: "flex", // Make it a flex container to manage its children (e.g., center empty state)
                    flexDirection: "column", // Stack children vertically
                    // Custom scrollbar styles are now handled globally by app.css
                    "&::-webkit-scrollbar": {
                      width: "4px",
                      display: "block",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "rgba(0, 0, 0, 0.2)",
                      },
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(0, 0, 0, 0.1) transparent",
                  }}
                >
                  {loading ||
                  youtubeLoading ||
                  articleLoading ||
                  templateLoading ? (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(248, 250, 252, 0.9)",
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <LoadingScreen
                        message={
                          activeTab === "youtube"
                            ? "Processing YouTube video..."
                            : activeTab === "article"
                            ? "Analyzing article content..."
                            : activeTab === "templates"
                            ? "Generating from template..."
                            : "Generating your post..."
                        }
                      />
                    </Box>
                  ) : subtitlesNotFound && activeTab === "youtube" ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          bgcolor: "rgba(239, 68, 68, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 3,
                        }}
                      >
                        <YouTubeIcon sx={{ fontSize: 48, color: "#ef4444" }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#2D3748",
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        No Subtitles Found
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#4A5568",
                          maxWidth: 400,
                          textAlign: "center",
                        }}
                      >
                        This video doesn't have subtitles available. Please try
                        another video or make sure the video has closed captions
                        enabled.
                      </Typography>
                    </Box>
                  ) : !generatedContent ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          bgcolor: "rgba(72, 144, 254, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 3,
                        }}
                      >
                        <SendIcon sx={{ fontSize: 48, color: "#4890fe" }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#2D3748",
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Ready to create your LinkedIn post
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#4A5568",
                          maxWidth: 400,
                          textAlign: "center",
                        }}
                      >
                        Fill in the form on the left with your topic and click
                        "Generate Post"
                      </Typography>
                    </Box>
                  ) : (
                    <LinkedInPreview
                      user={{ ...user, headline: userProfile?.headline }}
                      postImage={postImage}
                      onRemoveImage={() => setPostImage("")}
                      carouselFile={carouselFile}
                      onRemoveCarousel={() => setCarouselFile(null)}
                      sx={{
                        borderRadius: "8px",
                        overflow: "visible",
                        boxShadow: "none",
                        border: "none",
                        "& .preview-content": {
                          display: "flex",
                          flexDirection: "column",
                        },
                      }}
                    >
                      {isEditing ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%", // This height: 100% might be for the editor itself to fill LinkedInPreview
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            overflow: "hidden", // Editor container can hide its overflow
                          }}
                        >
                          <PostEditor
                            content={editedContent || generatedContent}
                            onChange={(newContent) => {
                              try {
                                const contentState = convertFromRaw(
                                  JSON.parse(newContent)
                                );
                                const plainText = contentState.getPlainText();
                                setEditedContent(plainText);
                              } catch (e) {
                                setEditedContent(newContent);
                              }
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 2,
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.6,
                            overflow: "visible",
                          }}
                        >
                          {" "}
                          {/* Ensure this box also doesn't try to scroll internally */}
                          <TypeWriter
                            text={editedContent || generatedContent}
                            speed={2}
                          />
                        </Box>
                      )}
                    </LinkedInPreview>
                  )}
                </Box>

                {generatedContent && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: { xs: 0.5, sm: 2 }, // Less gap on small screens
                      mt: 3,
                      pt: 2,
                      borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                      flexWrap: "nowrap", // Keep buttons in a single row
                      px: 1, // Add horizontal padding to ensure buttons don't touch edges on small screens
                    }}
                  >
                    {isEditing ? (
                      <>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => {
                            setIsEditing(false);
                            setEditedContent("");
                          }}
                          startIcon={<CloseIcon />}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: { xs: "0.75rem", sm: "0.9rem" }, // Smaller font on mobile
                            px: { xs: 1, sm: 2 }, // Less padding on small screens
                            height: { xs: "36px", sm: "40px" }, // Smaller height on mobile
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setGeneratedContent(editedContent);
                            setIsEditing(false);
                          }}
                          startIcon={<CheckIcon />}
                          sx={{
                            bgcolor: "#4890fe",
                            borderRadius: "6px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: { xs: "0.75rem", sm: "0.9rem" }, // Smaller font on mobile
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: "#3a7ad9",
                              boxShadow: "none",
                            },
                            px: { xs: 1, sm: 2 }, // Less padding on small screens
                            height: { xs: "36px", sm: "40px" }, // Smaller height on mobile
                          }}
                        >
                          Save Changes
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            setIsEditing(true);
                            setEditedContent(generatedContent);
                          }}
                          startIcon={<EditIcon />}
                          sx={{
                            borderColor: "#4890fe",
                            color: "#4890fe",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: { xs: "0.75rem", sm: "0.9rem" }, // Smaller font on mobile
                            "&:hover": {
                              borderColor: "#3a7ad9",
                              bgcolor: "rgba(72, 144, 254, 0.04)",
                            },
                            px: { xs: 1, sm: 2 }, // Less padding on small screens
                            height: { xs: "36px", sm: "40px" }, // Smaller height on mobile
                          }}
                        >
                          Edit Post
                        </Button>

                        {/* Add modification options button */}
                        <Button
                          variant="outlined"
                          onClick={(e) =>
                            setModificationAnchorEl(e.currentTarget)
                          }
                          startIcon={<TuneIcon />}
                          sx={{
                            borderColor: "#9c27b0",
                            color: "#9c27b0",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: { xs: "0.75rem", sm: "0.9rem" },
                            "&:hover": {
                              borderColor: "#7b1fa2",
                              bgcolor: "rgba(156, 39, 176, 0.04)",
                            },
                            px: { xs: 1, sm: 2 },
                            height: { xs: "36px", sm: "40px" },
                          }}
                        >
                          AI Enhance
                        </Button>

                        <Button
                          variant="contained"
                          onClick={handleShareToLinkedIn}
                          disabled={isSharing}
                          startIcon={
                            isSharing ? (
                              <CircularProgress size={20} />
                            ) : (
                              <LinkedInIcon />
                            )
                          }
                          sx={{
                            bgcolor: "#0A66C2",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: { xs: "0.75rem", sm: "0.9rem" }, // Smaller font on mobile
                            "&:hover": {
                              bgcolor: "#084482",
                            },
                            px: { xs: 1, sm: 2 }, // Less padding on small screens
                            height: { xs: "36px", sm: "40px" }, // Smaller height on mobile
                          }}
                        >
                          Share to LinkedIn
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          )}

          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {saveSuccess}
            </Alert>
          )}
        </Box>
      </Container>

      <Dialog
        open={openSuccessDialog}
        onClose={() => setOpenSuccessDialog(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontFamily: "var(--font-family)",
              fontWeight: 600,
            }}
          >
            Post Saved Successfully!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your post has been saved. You can view it in My Posts.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setOpenSuccessDialog(false)}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontFamily: "var(--font-family)",
                fontWeight: 500,
                fontSize: "0.9rem",
                color: "#4A5568",
                borderColor: "rgba(0, 0, 0, 0.15)",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "rgba(0, 0, 0, 0.25)",
                  backgroundColor: "rgba(0, 0, 0, 0.03)",
                  boxShadow: "none",
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenSuccessDialog(false);
                navigate("/dashboard/posts");
              }}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontFamily: "var(--font-family)",
                fontWeight: 500,
                fontSize: "0.9rem",
                bgcolor: "#5546ff",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#4435ee",
                  boxShadow: "none",
                },
              }}
            >
              View Posts
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {!user?.linkedin_access_token && (
        <LinkedInConnectDialog
          open={showLinkedInConnect}
          onClose={() => setShowLinkedInConnect(false)}
        />
      )}

      {/* Template Dialog for viewing full template */}
      <Dialog
        open={openTemplateDialog}
        onClose={() => setOpenTemplateDialog(false)}
        maxWidth="md"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            px: 3,
            py: 2,
          }}
        >
          <Typography
            variant="h6"
            className="component-heading"
            sx={{
              fontWeight: 600,
              color: "#2D3748",
              fontFamily: "var(--font-family)",
            }}
          >
            {fullTemplateTitle}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.8,
              color: "#4A5568",
              fontSize: "0.95rem",
            }}
          >
            {fullTemplateText.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < fullTemplateText.split("\n").length - 1 && <br />}
              </React.Fragment>
            ))}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}
        >
          <Button
            onClick={() => setOpenTemplateDialog(false)}
            sx={{
              textTransform: "none",
              fontFamily: "var(--font-family)",
              fontWeight: 500,
              fontSize: "0.9rem",
              color: "#4A5568",
              borderRadius: "6px",
              borderColor: "rgba(0, 0, 0, 0.15)",
              boxShadow: "none",
              "&:hover": {
                borderColor: "rgba(0, 0, 0, 0.25)",
                backgroundColor: "rgba(0, 0, 0, 0.03)",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Find the template by title and select it
              const template = templates[templateCategory].find(
                (t) => t.title === fullTemplateTitle
              );
              if (template) {
                handleUseTemplateLocal(template);
              }
            }}
            sx={{
              backgroundColor: "#4890fe",
              color: "white",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.9rem",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#3a7ad9",
                boxShadow: "none",
              },
            }}
          >
            Use This Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals for GenerateImage and GenerateCarousel */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh", m: 0 } }} // Ensure dialog paper takes height
      >
        <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
          {" "}
          {/* Ensure content fills dialog and manages overflow */}
          <GenerateImage
            isModalMode={true}
            onImageGenerated={handleImageAttached}
            initialPrompt={formData.topic || formData.prompt || ""}
            onClose={() => setShowImageModal(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCarouselModal}
        onClose={() => setShowCarouselModal(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "95vh", m: 0 } }}
      >
        <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
          <GenerateCarousel
            isModalMode={true}
            onCarouselGenerated={handleCarouselAttached}
            initialContent={generatedContent}
            onClose={() => setShowCarouselModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Attachment Confirmation Dialog */}
      <Dialog
        open={showAttachmentConfirmDialog}
        onClose={() => {
          setShowAttachmentConfirmDialog(false);
          setPendingAttachmentType(null);
        }}
      >
        <DialogTitle>Replace Existing Attachment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingAttachmentType === "image"
              ? "You already have a carousel attached. Attaching an image will remove the carousel. Do you want to continue?"
              : "You already have an image attached. Attaching a carousel will remove the image. Do you want to continue?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAttachmentConfirmDialog(false);
              setPendingAttachmentType(null);
            }}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAttachmentConfirm}
            variant="contained"
            sx={{
              bgcolor: "#4890fe",
              "&:hover": { bgcolor: "#3a7ad9" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay for modifications */}
      {modificationLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              AI is enhancing your post...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will take a few seconds
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Replace the Dialog with Menu */}
      <Menu
        anchorEl={modificationAnchorEl}
        open={Boolean(modificationAnchorEl)}
        onClose={() => setModificationAnchorEl(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            width: 280,
            maxWidth: "90vw",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            mt: -1,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#2D3748",
              mb: 1,
              fontSize: "0.95rem",
            }}
          >
            AI-Powered Enhancement
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontSize: "0.8rem" }}
          >
            Choose how to enhance your post:
          </Typography>
        </Box>

        <MenuItem
          onClick={() => handlePostModification("reduce_length")}
          sx={{
            px: 2,
            py: 1.5,
            "&:hover": {
              bgcolor: "rgba(72, 144, 254, 0.08)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: "rgba(72, 144, 254, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.9rem" }}>✂️</Typography>
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Make it Shorter"
            secondary="Reduce length, keep structure"
            primaryTypographyProps={{
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => handlePostModification("change_hook")}
          sx={{
            px: 2,
            py: 1.5,
            "&:hover": {
              bgcolor: "rgba(72, 144, 254, 0.08)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: "rgba(72, 144, 254, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.9rem" }}>🎣</Typography>
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Better Hook"
            secondary="More engaging opening"
            primaryTypographyProps={{
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => handlePostModification("add_hashtags")}
          sx={{
            px: 2,
            py: 1.5,
            "&:hover": {
              bgcolor: "rgba(72, 144, 254, 0.08)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: "rgba(72, 144, 254, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.9rem" }}>#️⃣</Typography>
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Add Hashtags"
            secondary="Add 3-4 relevant hashtags"
            primaryTypographyProps={{
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setModificationAnchorEl(null);
            setShowModificationOptions(true);
          }}
          sx={{
            px: 2,
            py: 1.5,
            "&:hover": {
              bgcolor: "rgba(72, 144, 254, 0.08)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: "rgba(72, 144, 254, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.9rem" }}>🎯</Typography>
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Custom Instructions"
            secondary="Give specific guidance"
            primaryTypographyProps={{
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
            }}
          />
        </MenuItem>
      </Menu>

      {/* Keep the custom instructions dialog for detailed input */}
      <Dialog
        open={showModificationOptions}
        onClose={() => {
          setShowModificationOptions(false);
          setCustomInstructions("");
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            px: 3,
            py: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#2D3748",
              fontFamily: "var(--font-family)",
            }}
          >
            Custom Instructions
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tell the AI how you'd like to modify your post:
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="How would you like to modify the post?"
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="e.g., Make it more casual, add more emojis, focus on the benefits, etc."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}
        >
          <Button
            onClick={() => {
              setShowModificationOptions(false);
              setCustomInstructions("");
            }}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              color: "#4A5568",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handlePostModification("custom", customInstructions);
              setShowModificationOptions(false);
            }}
            disabled={!customInstructions.trim()}
            sx={{
              bgcolor: "#4890fe",
              borderRadius: "6px",
              textTransform: "none",
              "&:hover": { bgcolor: "#3a7ad9" },
            }}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GeneratePost;
